import { DeleteOutlined, EditOutlined, LoginOutlined, LogoutOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Card, Timeline, Typography } from "antd";
import type { FC, ReactElement } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";

import type { ChangeLogEntryDto, RecentActivityDto, RevisionType } from "../types/Dashboard";
import { formatRelativeTime, getRevisionTypeColor } from "../utils/formatters";

const { Text, Paragraph } = Typography;

interface RecentActivityTimelineProps {
    activity: RecentActivityDto;
}

const getRevisionIcon = (type: RevisionType): ReactElement => {
    const iconMap: Record<RevisionType, ReactElement> = {
        INSERT: <PlusCircleOutlined />,
        UPDATE: <EditOutlined />,
        DELETE: <DeleteOutlined />,
        LOGIN: <LoginOutlined />,
        LOGOUT: <LogoutOutlined />,
    };

    return iconMap[type] || <EditOutlined />;
};

export const RecentActivityTimeline: FC<RecentActivityTimelineProps> = ({ activity }) => {
    const navigate = useNavigate();
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

    const toggleExpanded = (id: number) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedItems(newExpanded);
    };

    const handleSeeAll = () => {
        navigate("/user-logs");
    };

    const timelineItems = activity.recentChanges.map((change: ChangeLogEntryDto) => {
        const isExpanded = expandedItems.has(change.id);
        const shouldTruncate = change.changes.length > 100;

        return {
            color: getRevisionTypeColor(change.revisionType as RevisionType),
            dot: getRevisionIcon(change.revisionType as RevisionType),
            children: (
                <div key={change.id}>
                    <div style={{ marginBottom: 4 }}>
                        <Text strong>{change.className}</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            • {change.revisionType}
                        </Text>
                    </div>
                    <Paragraph ellipsis={shouldTruncate && !isExpanded ? { rows: 2 } : false} style={{ marginBottom: 4 }}>
                        {change.changes}
                    </Paragraph>
                    {shouldTruncate && (
                        <button
                            type="button"
                            onClick={() => toggleExpanded(change.id)}
                            style={{
                                fontSize: 12,
                                background: "none",
                                border: "none",
                                color: "#1890ff",
                                cursor: "pointer",
                                padding: 0,
                            }}
                        >
                            {isExpanded ? "Show less" : "Show more"}
                        </button>
                    )}
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            by {change.username} • {formatRelativeTime(change.changed)}
                        </Text>
                    </div>
                </div>
            ),
        };
    });

    return (
        <Card
            title="Recent Activity"
            extra={<Button onClick={handleSeeAll}>See all</Button>}
            style={{ backgroundColor: "#FFFFFF", height: "100%" }}
        >
            <Timeline items={timelineItems} />
        </Card>
    );
};
